"use client";

import React from 'react'
import { motion, useMotionValue, useTransform } from 'framer-motion'
import { Card } from '@/components/ui/card'
import { TypographyH3, TypographyP } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { getServices, getUI } from '@/lib/config'
import { Grid3X3, Layers, Gem, Hammer, ChevronRight, Briefcase, Check } from 'lucide-react'
import { CustomImage } from '@/components/ui/image'

interface ServiceSnapshotCardProps {
  title: string
  description: string
  offerings?: string[]
  href?: string
  className?: string
  iconImageSrc?: string
  variant?: 'default' | 'centered'
  ctaText?: string
  centerTitle?: boolean
}

function getIconForService(iconKey?: string) {
  switch (iconKey) {
    case 'patio':
      return Grid3X3
    case 'concrete':
      return Layers
    case 'stone':
      return Gem
    case 'hardscape':
      return Hammer
    case 'advisory':
    case 'users':
      return Briefcase
    default:
      return Hammer
  }
}

export function ServiceSnapshotCard({
  title,
  description,
  offerings = [],
  href = '#',
  className,
  iconImageSrc,
  variant = 'default',
  ctaText: propCtaText,
  centerTitle
}: ServiceSnapshotCardProps) {
  const services = getServices()
  const { serviceIconSize = 'h-10 w-10' } = getUI()
  // Try to find matching service config, but don't force it if we have props
  const match = services.find((s) => s.href === href || s.title === title)

  const Icon = getIconForService(match?.icon)
  const ctaText = propCtaText ?? match?.cta ?? 'Learn more'

  // Subtle parallax for the overlay CTA icon
  const y = useMotionValue(0)
  const overlayY = useTransform(y, [0, 1], [16, 0])

  const isCentered = variant === 'centered';

  return (
    <a href={href} className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded-xl h-full">
      <motion.div
        initial={{ scale: 1 }}
        whileHover={{ scale: 1.01 }}
        transition={{ type: 'spring', stiffness: 250, damping: 20 }}
        className="h-full"
      >
        <Card
          className={cn(
            'relative overflow-hidden bg-white text-card-foreground shadow-sm rounded-[3px] h-full',
            'flex flex-col p-6 md:p-8',
            isCentered ? 'items-center text-center' : 'justify-between min-h-[180px]',
            className,
          )}
          onMouseEnter={() => y.set(1)}
          onMouseLeave={() => y.set(0)}
        >
          <div className={cn("relative z-10 flex flex-col gap-4 mb-4 w-full", (isCentered || centerTitle) ? "items-center" : "items-start")}>
            {iconImageSrc ? (
              <div className="flex justify-center mb-2">
                <CustomImage
                  src={iconImageSrc}
                  alt={title}
                  width={78}
                  height={78}
                  className="h-[60px] md:h-[78px] w-auto object-contain"
                  containerClassName="flex justify-center"
                  withBackground={false}
                  rounded={false}
                />
              </div>
            ) : (
              <Icon className={cn(serviceIconSize, 'text-primary')} />
            )}

            <TypographyH3 className={cn(
              "font-normal",
              isCentered ? "text-[#391d5a] text-base md:text-lg lg:text-[18px]" : "text-lg font-bold text-foreground",
              centerTitle && "text-center"
            )}>
              {title}
            </TypographyH3>

            <TypographyP className={cn(
              "text-[#080808]",
              isCentered ? "text-xs md:text-sm lg:text-[13px] leading-[21px]" : "text-sm text-foreground/80"
            )}>
              {description}
            </TypographyP>

            {offerings.length > 0 && (
              <ul className={cn("space-y-2 w-full", isCentered ? "text-left" : "")}>
                {offerings.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-[#080808]">
                    <Check className="h-4 w-4 text-[#8dc63f] shrink-0 mt-[2px]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Button for centered variant */}
          {isCentered && (
            <div className="mt-auto pt-2 w-full md:w-auto">
              <div className={cn(
                "bg-[#8dc63f] text-white text-sm md:text-base lg:text-[15.939px] h-[40px] rounded-[5px] hover:bg-[#8dc63f]/90 w-full md:w-auto flex items-center justify-center px-4 transition-colors",
              )}>
                {ctaText}
              </div>
            </div>
          )}

          {/* Hover overlay - only for default variant */}
          {!isCentered && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileHover={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="absolute inset-0 z-20 bg-primary/95 text-primary-foreground"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-primary" />
              <div className="relative z-10 h-full w-full flex flex-col justify-center items-center text-center gap-3 p-6">
                <TypographyH3 className="text-xl font-bold">{title}</TypographyH3>
                <motion.span style={{ y: overlayY }} className="inline-flex items-center gap-2 text-sm font-semibold">
                  {ctaText}
                  <ChevronRight className="h-4 w-4" />
                </motion.span>
              </div>
            </motion.div>
          )}

          {/* Border highlight on hover - only for default variant */}
          {!isCentered && (
            <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-transparent group-hover:ring-primary/40" />
          )}
        </Card>
      </motion.div>
    </a>
  )
}

